/**
 * useDuplicateTarget — pick a provider + campaign + flow and apply the copied
 * recipe to it (creating a config if the target has none yet).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { SearchableSelectOption } from '@/components/ui/form/input/SearchableSelect';
import {
  upsertConfig,
  type FlowOptimizerProvider,
} from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import { useTrackerCampaignQueries } from '@/features/optimizer-center/hooks/useTrackerCampaignQueries';
import { useToast } from '@/providers/ToastProvider';

import { buildPasteBody } from './build-paste-body';
import type { CopiedOptimizerConfig, PasteFieldKey } from './optimizer-clipboard.types';

type Opt = SearchableSelectOption<string>;
const toOptions = (items?: { id: string; name: string }[]): Opt[] =>
  (items ?? []).map((i) => ({ value: i.id, label: i.name }));

export function useDuplicateTarget(copied: CopiedOptimizerConfig, onDone: () => void) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<FlowOptimizerProvider>(copied.source.provider);
  const [campaignId, setCampaignId] = useState('');
  const [flowId, setFlowId] = useState('');

  const { campaigns: campaignsQuery, flows: flowsQuery } = useTrackerCampaignQueries(
    provider,
    campaignId,
  );

  const campaigns = useMemo(() => toOptions(campaignsQuery.data), [campaignsQuery.data]);
  const flows = useMemo(() => toOptions(flowsQuery.data), [flowsQuery.data]);

  const changeProvider = (next: FlowOptimizerProvider) => {
    setProvider(next);
    setCampaignId('');
    setFlowId('');
  };
  const changeCampaign = (id: string) => {
    setCampaignId(id);
    setFlowId('');
  };

  const applyMut = useMutation({
    mutationFn: (fields: Set<PasteFieldKey>) =>
      upsertConfig(campaignId, buildPasteBody(copied.recipe, flowId, fields), provider),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: optimizerCenterKeys.configs(provider) });
      const name = campaigns.find((c) => c.value === campaignId)?.label ?? campaignId;
      toast.success('Config applied', `Saved to "${name}".`);
      onDone();
    },
    onError: () => toast.error('Could not apply', 'The config was not saved.'),
  });

  return {
    provider,
    setProvider: changeProvider,
    campaignId,
    setCampaignId: changeCampaign,
    flowId,
    setFlowId,
    campaigns,
    flows,
    loadingCampaigns: campaignsQuery.isLoading,
    loadingFlows: Boolean(campaignId) && flowsQuery.isLoading,
    canApply: Boolean(campaignId) && Boolean(flowId),
    isApplying: applyMut.isPending,
    apply: (fields: Set<PasteFieldKey>) => applyMut.mutate(fields),
  };
}
